import { ENGINE_URL, BASE_URL } from "./service.config.js";
import { GQLNow } from "gql-now";

let cachedUsers = {
  LIVE: null,
  SIMULATION: null,
};
let cachedUserMaps = {
  LIVE: new Map(),
  SIMULATION: new Map(),
};

/**
 * Build quick-lookup Map for fast UCC / client_tag / user_id matching
 */
export function buildUserMap(users = []) {
  const map = new Map();
  users.forEach((u) => {
    if (u.ucc) map.set(u.ucc.toUpperCase(), u);
    if (u.client_tag) map.set(u.client_tag.toUpperCase(), u);
    if (u.user_id) map.set(u.user_id, u);
    if (u._id) map.set(u._id, u);
    if (u.mobile) map.set(u.mobile, u);
  });
  return map;
}

/**
 * Fetch DB Users with caching and automatic REST -> GQL fallback
 */
export async function fetchUsersDirectory(env = "LIVE", forceRefresh = false) {
  const normalizedEnv = (env || "LIVE").toUpperCase();

  if (!forceRefresh && cachedUsers[normalizedEnv] && cachedUsers[normalizedEnv].length > 0) {
    return {
      users: cachedUsers[normalizedEnv],
      userMap: cachedUserMaps[normalizedEnv],
    };
  }

  let users = [];

  // 1. Try REST Engine endpoint
  try {
    const endpoint = normalizedEnv === "SIMULATION" ? `${ENGINE_URL}/sim/users` : `${ENGINE_URL}/users`;
    const res = await fetch(endpoint);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) {
        users = data;
      } else if (data && Array.isArray(data.users)) {
        users = data.users;
      }
    }
  } catch (e) {
    console.warn(`REST users fetch failed for ${normalizedEnv}, trying GraphQL fallback:`, e);
  }

  // 2. Fallback to GQL if REST is empty
  if (users.length === 0) {
    try {
      const token = localStorage.getItem("token");
      const obj = new GQLNow(`${BASE_URL}/graphql/trdr-admin-services`, {
        "x-trdr-org": "60e6cd9df9c32f00072b71f8",
        "x-access-token": token,
      });
      const gqlRes = await obj.query(
        "fetch_all_users",
        {},
        "all_users {_id user_id fullName firstName lastName mobile emailAddress pan ucc client_tag trdr_id demat_acc_number account_status master_opt_in amount investment_capital tier custom_lot dwn_avg_qty multiplier}"
      );
      if (
        gqlRes &&
        gqlRes.data &&
        gqlRes.data.fetch_all_users &&
        Array.isArray(gqlRes.data.fetch_all_users.all_users)
      ) {
        users = gqlRes.data.fetch_all_users.all_users.map((u) => {
          const resolvedName =
            (u.fullName && u.fullName.trim()) ||
            `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
            "";
          return {
            user_id: u.user_id || u._id,
            fullName: resolvedName,
            mobile: u.mobile || "",
            emailAddress: u.emailAddress || "",
            pan: u.pan || "",
            ucc: u.ucc || "",
            client_tag: u.client_tag || u.ucc || "",
            trdr_id: u.trdr_id || "",
            demat_acc_number: u.demat_acc_number || "",
            account_status: u.account_status || "approved",
            master_opt_in: Boolean(u.master_opt_in),
            amount: u.amount !== undefined && u.amount !== null ? Number(u.amount) : (u.investment_capital !== undefined ? Number(u.investment_capital) : 0),
            tier: u.tier || null,
            custom_lot: u.custom_lot || null,
            dwn_avg_qty: u.dwn_avg_qty || 1,
            multiplier: u.multiplier || 1.0,
          };
        });
      }
    } catch (gqlErr) {
      console.error("Failed to fetch users from GraphQL fallback:", gqlErr);
    }
  }

  const userMap = buildUserMap(users);
  cachedUsers[normalizedEnv] = users;
  cachedUserMaps[normalizedEnv] = userMap;

  return { users, userMap };
}

export function clearUserDirectoryCache() {
  cachedUsers = { LIVE: null, SIMULATION: null };
  cachedUserMaps = { LIVE: new Map(), SIMULATION: new Map() };
}
